import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect } from 'react';

import { CampaignInfo, getCampaignInfo } from '../../api/campaigns';
import { useIsLoggedIn } from '../../contexts/authentication';

const CampaignInfoContext = createContext<CampaignInfo | undefined>(undefined);

export const useCampaignInfo = () => useContext(CampaignInfoContext);

const useCampaignInfoQuery = (campaignId: string) => {
  return useQuery({
    queryKey: ['campaignInfo', campaignId],
    queryFn: () => getCampaignInfo(campaignId),
    retry: false,
  });
};

const CampaignInfoFetchingContext = createContext<boolean>(false);
export const useCampaignInfoFetching = () =>
  useContext(CampaignInfoFetchingContext);

export function CampaignInfoProvider({
  campaignId,
  children,
  errorComponent,
}: {
  campaignId: string;
  children: React.ReactNode;
  errorComponent?: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const campaignInfoQuery = useCampaignInfoQuery(campaignId);
  const isLoggedIn = useIsLoggedIn();
  useEffect(() => {
    if (!isLoggedIn) {
      queryClient.invalidateQueries({
        queryKey: ['campaignInfo', campaignId],
      });
    }
  }, [isLoggedIn]);
  if (campaignInfoQuery.isError) {
    return errorComponent;
  }
  return (
    <CampaignInfoFetchingContext.Provider
      value={campaignInfoQuery.isFetching || campaignInfoQuery.isRefetching}
    >
      <CampaignInfoContext.Provider value={campaignInfoQuery.data}>
        {children}
      </CampaignInfoContext.Provider>
    </CampaignInfoFetchingContext.Provider>
  );
}
